/**
 * Pairing the two legs of a knockout tie, and the aggregate they add up to
 * (specs/champions-league AC-6.7, AC-6.9).
 *
 * A knockout tie is two fixtures and ONE outcome. Nothing in the payload says "these two rows
 * are the same tie", so it is derived here: the sides swap between legs, which makes the
 * unordered team pair the only stable key. Within a stage that key is unique — no two clubs
 * meet twice in the same round of the same competition.
 *
 * Pure — no React, no queries.
 */

import type { ApiMatch } from '../../worldcup/helpers/worldcupPicksData.helper';
import { tiePairKey } from './tournamentBracket.helper';

/** One side's contribution to a tie. */
export interface TieSide {
  teamId: string | null;
  teamName: string;
  /** Goals in each leg, in leg order. Entries are null for a leg not yet played. */
  legGoals: (number | null)[];
  /** Sum over the legs that have been played, or null when none have. */
  aggregate: number | null;
  /** Shootout score, from the leg that went to penalties. */
  penalties: number | null;
}

export interface KnockoutTie {
  /** Stable key: stage plus the sorted team pair. */
  key: string;
  stage: string;
  stageLabel: string;
  /** The tie's fixtures in leg order (`displayOrder`). One entry for a single-legged tie. */
  legs: ApiMatch[];
  home: TieSide;
  away: TieSide;
  /** True once every leg has finished. */
  isComplete: boolean;
  /** True when the tie is played over two legs. */
  isTwoLegged: boolean;
}

/**
 * The score an AGGREGATE is built from: the FINAL score, extra time included.
 *
 * Deliberately NOT the 90-minute score that picks settle on (AC-14.6). An aggregate answers
 * "who goes through", and extra time in the second leg counts towards exactly that. The two
 * questions have different answers and the spec requires that neither be printed as the other.
 */
function finalGoals(match: ApiMatch, side: 'home' | 'away'): number | null {
  const value = side === 'home' ? match.homeScore : match.awayScore;
  return typeof value === 'number' ? value : null;
}

/**
 * Unordered, so leg 1's "A v B" and leg 2's "B v A" collapse to one key. Scoped by stage
 * because the same two clubs can legitimately meet again in a later round.
 *
 * The pair half is `tiePairKey`, not a second sort-and-join: "are these the same tie?" already
 * had an answer here and two of them would be one too many.
 */
function tieKey(match: ApiMatch): string {
  const a = match.homeTeamId ?? match.homeTeam;
  const b = match.awayTeamId ?? match.awayTeam;
  return `${match.stage}::${tiePairKey(a, b)}`;
}

function sumPlayed(goals: (number | null)[]): number | null {
  const played = goals.filter((g): g is number => typeof g === 'number');
  return played.length === 0 ? null : played.reduce((a, b) => a + b, 0);
}

/**
 * Group a competition's knockout fixtures into ties.
 *
 * `stages` limits which fixtures are considered — the league phase is not a knockout and must
 * never be folded into ties. Ordering within a tie, and between ties, is `displayOrder`, which
 * the payload assigns over date-sorted fixtures.
 */
export function buildKnockoutTies(
  matches: readonly ApiMatch[],
  stages: readonly string[],
): KnockoutTie[] {
  const knockoutStages = new Set(stages);
  const byKey = new Map<string, ApiMatch[]>();

  for (const match of matches) {
    if (!match.stage || !knockoutStages.has(match.stage)) continue;
    const key = tieKey(match);
    const bucket = byKey.get(key);
    if (bucket) bucket.push(match);
    else byKey.set(key, [match]);
  }

  const ties: KnockoutTie[] = [];
  for (const [key, bucket] of byKey.entries()) {
    const legs = [...bucket].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
    const first = legs[0];

    // The tie's "home" is the first leg's home side; leg 2 swaps, so goals are read per leg
    // against the tie's orientation rather than against each fixture's own.
    const homeId = first.homeTeamId ?? first.homeTeam;

    const homeGoals: (number | null)[] = [];
    const awayGoals: (number | null)[] = [];
    let homePens: number | null = null;
    let awayPens: number | null = null;

    for (const leg of legs) {
      const legHomeIsTieHome = (leg.homeTeamId ?? leg.homeTeam) === homeId;
      homeGoals.push(finalGoals(leg, legHomeIsTieHome ? 'home' : 'away'));
      awayGoals.push(finalGoals(leg, legHomeIsTieHome ? 'away' : 'home'));

      const pensHome = typeof leg.penaltyHomeScore === 'number' ? leg.penaltyHomeScore : null;
      const pensAway = typeof leg.penaltyAwayScore === 'number' ? leg.penaltyAwayScore : null;
      if (pensHome != null && pensAway != null) {
        homePens = legHomeIsTieHome ? pensHome : pensAway;
        awayPens = legHomeIsTieHome ? pensAway : pensHome;
      }
    }

    ties.push({
      key,
      stage: first.stage as string,
      stageLabel: first.stageLabel ?? '',
      legs,
      home: {
        teamId: first.homeTeamId ?? null,
        teamName: first.homeTeam,
        legGoals: homeGoals,
        aggregate: sumPlayed(homeGoals),
        penalties: homePens,
      },
      away: {
        teamId: first.awayTeamId ?? null,
        teamName: first.awayTeam,
        legGoals: awayGoals,
        aggregate: sumPlayed(awayGoals),
        penalties: awayPens,
      },
      isComplete: legs.every((l) => l.status === 'finished'),
      isTwoLegged: legs.length > 1,
    });
  }

  return ties.sort(
    (a, b) => (a.legs[0].displayOrder ?? 0) - (b.legs[0].displayOrder ?? 0),
  );
}

/**
 * The aggregate to state on ONE fixture's card, oriented to that fixture's own home/away
 * (AC-6.9), or null when there is nothing worth stating.
 *
 * Null for a first leg — there is nothing to aggregate yet — and for a single-legged tie,
 * where the aggregate would only repeat the score above it.
 */
export function aggregateForMatch(
  match: ApiMatch,
  ties: readonly KnockoutTie[],
): { home: number; away: number } | null {
  const tie = ties.find((t) => t.legs.some((l) => l.matchId === match.matchId));
  if (!tie || !tie.isTwoLegged) return null;

  // Nothing to aggregate on the opening leg.
  const legIndex = tie.legs.findIndex((l) => l.matchId === match.matchId);
  if (legIndex <= 0) return null;

  if (tie.home.aggregate == null || tie.away.aggregate == null) return null;

  // Orient to THIS fixture: the second leg's home side is the tie's away side.
  const thisHomeIsTieHome = (match.homeTeamId ?? match.homeTeam) === (tie.home.teamId ?? tie.home.teamName);
  return thisHomeIsTieHome
    ? { home: tie.home.aggregate, away: tie.away.aggregate }
    : { home: tie.away.aggregate, away: tie.home.aggregate };
}
